import { useMutation } from "@tanstack/react-query";

import { toApiError } from "../../lib/apiError";
import { parseImportDiagnostics, type ImportDiagnostic } from "../../lib/importDiagnostics";
import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

type PreviewImportResponse = components["schemas"]["PreviewImportResponse"];

/**
 * A validate failure that carries the server's structured diagnostics.
 *
 * Without this the `diagnostics` array is discarded at the hook boundary and the
 * page can only show prose, so every guided remedy would degrade to a toast.
 * Mirrors the ImportQuotaExceededError pattern in useCommitImport.
 */
export class ImportDiagnosticsError extends Error {
  readonly diagnostics: ImportDiagnostic[];

  constructor(message: string, diagnostics: ImportDiagnostic[]) {
    super(message);
    this.name = "ImportDiagnosticsError";
    this.diagnostics = diagnostics;
  }
}

export type ValidateImportVariables = {
  file: File;
  broker?: string;
  /**
   * The account this import is headed for. REQUIRED.
   *
   * Two jobs. It lets validate SHOW the account-mask binding before the user
   * commits -- the server only reads it to compare, and validate never WRITES a
   * binding, because a user who uploads a file, looks at the preview and walks
   * away must not have bound the account to a statement they never imported. A
   * genuine mismatch comes back as the same 400 + diagnostics the commit gives,
   * through the same server-side comparison, so the two steps cannot disagree.
   *
   * It is also how the server knows which TIMEZONE to read the statement's wall
   * clocks in (ADR 0039 D9). A thinkorswim export prints times with no timezone
   * in the file, and the zone is per-account -- so the per-fill execution times
   * in this response cannot be computed without it. That is why the field went
   * from optional to required: a preview that guessed the zone would show
   * confidently wrong times, which is the defect this change removes.
   */
  accountId: string;
};

export function useValidateImport() {
  const client = useApiClient();
  return useMutation<PreviewImportResponse, Error, ValidateImportVariables>({
    mutationFn: async ({ file, broker, accountId }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (broker !== undefined) {
        formData.append("broker", broker);
      }
      formData.append("account_id", accountId);
      const { data, error, response } = await client.POST("/api/imports/validate", {
        body: { file, broker: broker ?? "tos", account_id: accountId } as unknown as {
          file: string;
          account_id: string;
          broker: string;
        },
        bodySerializer: () => formData,
      });
      if (error !== undefined) {
        if (response.status === 413) {
          throw new Error("File is too large. Try a smaller CSV.");
        }
        const message = toApiError(error, "Failed to validate import.");
        const diagnostics = parseImportDiagnostics(error);
        if (diagnostics.length > 0) {
          throw new ImportDiagnosticsError(message, diagnostics);
        }
        throw new Error(message);
      }
      return data;
    },
  });
}
